import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Sparkles } from "lucide-react";

/**
 * BillingPastDueBanner — polls billing status on mount and shows a
 * calm-but-visible banner when Stripe marks the subscription as
 * `past_due` (card declined, inside the retry grace window before
 * Stripe auto-cancels ~2-3 weeks later).
 *
 * Also surfaces the `trialing` state for families on the 14-day Plus
 * free trial — calm, friendly, never alarming.
 *
 * Tapping "Update card" opens the Stripe Customer Portal so they can
 * fix billing without leaving the app UI mental model. Dismissable
 * per-session so a family that's already aware isn't nagged every page
 * load — but it comes back on reload.
 */
function daysLeft(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function BillingPastDueBanner() {
  const [mode, setMode] = useState(null); // "past_due" | "trialing" | null
  const [tier, setTier] = useState(null);
  const [trialEnds, setTrialEnds] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const pastDueDismissed = (() => {
      try {
        return sessionStorage.getItem("ff_past_due_dismissed") === "1";
      } catch {
        return false;
      }
    })();
    const trialDismissed = (() => {
      try {
        return sessionStorage.getItem("ff_trial_dismissed") === "1";
      } catch {
        return false;
      }
    })();
    api.billingStatus()
      .then((s) => {
        if (!alive) return;
        if (s?.subscription_status === "past_due" && !pastDueDismissed) {
          setTier(s.tier || null);
          setMode("past_due");
        } else if (s?.subscription_status === "trialing" && s?.trial_ends_at && !trialDismissed) {
          setTier(s.tier || null);
          setTrialEnds(s.trial_ends_at);
          setMode("trialing");
        }
      })
      .catch(() => {/* never fail the dashboard over a billing poll */});
    return () => { alive = false; };
  }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { url } = await api.billingPortal(origin);
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Couldn't open billing portal");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(
        mode === "trialing" ? "ff_trial_dismissed" : "ff_past_due_dismissed",
        "1",
      );
    } catch {/* ignore */}
    setMode(null);
  };

  if (!mode) return null;

  if (mode === "trialing") {
    const left = daysLeft(trialEnds);
    return (
      <div
        className="mb-5 rounded-xl border border-[color:var(--line)] bg-[color:var(--bg-soft)] p-4 flex flex-wrap items-start gap-3"
        data-testid="billing-trial-banner"
      >
        <div className="w-9 h-9 rounded-full bg-[color:var(--surface)] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-tiny text-[color:var(--ink-3)]">Free trial</div>
          <h3 className="font-display-italic text-xl leading-tight mt-0.5">
            {left !== null && left <= 0
              ? "Your Plus trial ends today"
              : left === 1
                ? "Your Plus trial ends tomorrow"
                : left !== null
                  ? `${left} days left of your Plus trial`
                  : "Your Plus trial is active"}
          </h3>
          <p className="text-[13px] leading-snug text-[color:var(--ink-2)] mt-1">
            You're enjoying everything in Plus — extra members, custom photo
            avatars, full mood &amp; sleep history. After the trial, your card
            is charged $3.99/month. Cancel anytime in the billing portal.
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            type="button"
            onClick={dismiss}
            className="ff-btn flex-1 sm:flex-none"
            data-testid="billing-trial-dismiss"
          >
            Got it
          </button>
          <button
            type="button"
            onClick={openPortal}
            disabled={busy}
            className="ff-btn ff-btn-primary flex-1 sm:flex-none"
            data-testid="billing-trial-manage"
          >
            {busy ? "Opening…" : "Manage billing"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mb-5 rounded-xl border border-[color:var(--line)] bg-[color:var(--bg-soft)] p-4 flex flex-wrap items-start gap-3"
      data-testid="billing-past-due-banner"
    >
      <div className="w-9 h-9 rounded-full bg-[color:var(--surface)] flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="w-4 h-4" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-tiny text-[color:var(--ink-3)]">Card declined</div>
        <h3 className="font-display-italic text-xl leading-tight mt-0.5">
          Your {tier ? `${tier.toUpperCase()} ` : ""}renewal didn't go through
        </h3>
        <p className="text-[13px] leading-snug text-[color:var(--ink-2)] mt-1">
          Stripe will retry for about a week, then the family drops back
          to Free automatically. Update your card now to avoid losing
          houses and members.
        </p>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto">
        <button
          type="button"
          onClick={dismiss}
          className="ff-btn flex-1 sm:flex-none"
          data-testid="billing-past-due-dismiss"
        >
          Later
        </button>
        <button
          type="button"
          onClick={openPortal}
          disabled={busy}
          className="ff-btn ff-btn-primary flex-1 sm:flex-none"
          data-testid="billing-past-due-update"
        >
          {busy ? "Opening…" : "Update card"}
        </button>
      </div>
    </div>
  );
}
